import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import client from '@/apolloClient';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import DatePickerField from '@/components/fields/DatePickerField';
import SelectField from '@/components/fields/SelectField';
import SwitchField from '@/components/fields/SwitchField';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import type { AssessmentRunOption } from '../../shared/utils';
import WeightInnerForm from '../WeightInnerForm';
import { baseWeightPerformanceFormSchema, type EditWeightPerformanceFormData } from '../WeightPerformanceFormConfig';
import type { WeightPerformanceRowData } from '../WeightPerformanceTab';
import { UpdatePerformanceWeightResultMutation } from './UpdatePerformanceWeightResult.graphql';
import '@/utils/notifications';

interface EditPerformanceWeightModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    rowData: WeightPerformanceRowData | null;
    assessmentRunOptions: AssessmentRunOption[];
}

const EditPerformanceWeightModal = ({
    open,
    onClose,
    rowData,
    assessmentRunOptions,
}: EditPerformanceWeightModalProps) => {
    const { t } = useTranslation('performanceResult');

    const form = useForm<EditWeightPerformanceFormData>({
        mode: 'onChange',
        resolver: zodResolver(baseWeightPerformanceFormSchema),
        defaultValues: {
            weightKg: rowData?.weightKg ?? undefined,
            date: rowData?.assessmentDate ?? undefined,
            isTiedToAssessmentRun: rowData?.isTiedToAssessmentRun ?? false,
            assessmentRunId: rowData?.assessmentRunId ?? undefined,
        },
    });

    const isTiedToAssessmentRun = form.watch('isTiedToAssessmentRun');

    useEffect(() => {
        if (rowData?.isTiedToAssessmentRun && rowData.assessmentRunId) {
            form.setValue('assessmentRunId', rowData.assessmentRunId);
        }
    }, [form, rowData]);

    const handleSubmit = async () => {
        if (!(await form.trigger()) || !rowData) {
            return;
        }
        const values = form.getValues();

        try {
            const { data } = await client.mutate({
                mutation: UpdatePerformanceWeightResultMutation,
                variables: {
                    id: rowData.id,
                    input: {
                        assessmentRunId: values.isTiedToAssessmentRun ? values.assessmentRunId || undefined : undefined,
                        result: {
                            weightKg: values.weightKg ?? undefined,
                        },
                        completedAt: values.date,
                    },
                },
            });

            if (data?.updateWeightResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.updateWeightResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage = specificErrorField?.message || t('updateError.description', { type: 'Weight' });

                showErrorNotification({
                    message: t('updateError.title', { type: 'Weight' }),
                    description: errorMessage,
                });

                throw new Error(errorMessage);
            } else {
                showSuccessNotification({
                    message: t('updateSuccess.title', { type: 'Weight' }),
                    description: t('updateSuccess.message', { type: 'Weight' }),
                });

                onClose(true);
            }
        } catch (error) {
            console.error('Error updating Weight performance result:', error);
            throw error;
        }
    };

    const modalFooter = (
        <div className="mt-5 flex w-full justify-end">
            <div className="flex space-x-2">
                <Button variant="secondary" onClick={() => onClose()}>
                    {t('weight.editModal.cancelBtn')}
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    {t('weight.editModal.saveBtn')}
                </Button>
            </div>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={t('weight.editModal.title')}
                footer={modalFooter}
                showCloseButton={false}
                className="w-full sm:w-xl"
                largeTitle
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-3">
                            <DatePickerField name="date" label={t('weight.form.assessmentDate')} required />
                        </div>
                        <WeightInnerForm />
                        <div className="sm:col-span-3">
                            <SwitchField
                                name="isTiedToAssessmentRun"
                                label={t('soc.form.tieToAssessmentRun', {
                                    defaultValue: 'Tie To Assessment Run',
                                })}
                            />
                        </div>
                        {isTiedToAssessmentRun && (
                            <div className="sm:col-span-3">
                                <SelectField
                                    name="assessmentRunId"
                                    label={t('filter.assessmentRun')}
                                    placeholder={t('filter.assessmentRunPlaceholder')}
                                    options={assessmentRunOptions}
                                    required
                                />
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default EditPerformanceWeightModal;
